/**
 * Admin V3 — Unified admin (Phase 1 skeleton)
 *
 * Mounted at: /admin/* (canonical, B3-12; /admin-v3/* redirects here — see App.js)
 *
 * SCOPE FOR PHASE 1:
 *   - Shell renders TopBar + Sidebar (legacy palette preserved verbatim)
 *   - 30 routes registered (15 modules × 1-2 routes each + sub-views +
 *     index redirect + catch-all)
 *   - Every route renders <ModulePlaceholder>; zero business logic
 *   - Zero backend calls; zero mutations; zero financial side effects
 *   - Foundation hooks (useAdminQuery, useAdminMutation, useToast,
 *     usePermissions) are defined but NOT consumed in Phase 1
 *
 * LEGACY UNTOUCHED:
 *   AdminDashboard.js, AdminControlTower.js, AdminControlTowerV2.js, and
 *   the /admin-v2 preview tree remain operational. V3 is an isolated
 *   parallel route; cutover to /admin/* happens at Phase 5.
 */

import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AdminV3Shell from '../admin-v3/layout/AdminV3Shell';
import ModulePlaceholder from '../admin-v3/shared/components/ModulePlaceholder';
import OverviewPage from '../admin-v3/modules/overview/OverviewPage';
import BookingsListPage from '../admin-v3/modules/bookings/BookingsListPage';
import UnassignedQueuePage from '../admin-v3/modules/bookings/UnassignedQueuePage';
import BookingDetailPage from '../admin-v3/modules/bookings/BookingDetailPage';
import DriversListPage from '../admin-v3/modules/drivers/DriversListPage';
import OnboardingListPage from '../admin-v3/modules/onboarding/OnboardingListPage';
import OperationsLivePage from '../admin-v3/modules/operations/OperationsLivePage';
import OperationsAuditPage from '../admin-v3/modules/operations/OperationsAuditPage';
import OperationsNotificationsPage from '../admin-v3/modules/operations/OperationsNotificationsPage';
import OperationsHealthPage from '../admin-v3/modules/operations/OperationsHealthPage';
import IncidentsListPage from '../admin-v3/modules/incidents/IncidentsListPage';
import ContactsListPage from '../admin-v3/modules/contacts/ContactsListPage';
import BillingInvoicesPage from '../admin-v3/modules/billing/BillingInvoicesPage';
import BillingInvoiceDetailPage from '../admin-v3/modules/billing/BillingInvoiceDetailPage';
import AvoirListPage from '../admin-v3/modules/billing/AvoirListPage';
import AvoirDetailPage from '../admin-v3/modules/billing/AvoirDetailPage';
import DevisListPage from '../admin-v3/modules/devis/DevisListPage';
import DevisDetailPage from '../admin-v3/modules/devis/DevisDetailPage';
import BdcListPage from '../admin-v3/modules/commande/BdcListPage';
import BdcDetailPage from '../admin-v3/modules/commande/BdcDetailPage';
import PartnersListPage from '../admin-v3/modules/partners/PartnersListPage';
import CorporateListPage from '../admin-v3/modules/corporate/CorporateListPage';
import FinancialLedgerPage from '../admin-v3/modules/financial/FinancialLedgerPage';
import FinancialWithdrawalsPage from '../admin-v3/modules/financial/FinancialWithdrawalsPage';
import FinancialPayoutsPage from '../admin-v3/modules/financial/FinancialPayoutsPage';
import FinancialHubPage from '../admin-v3/modules/financial/FinancialHubPage';
import SubscriptionsDisabledPage from '../admin-v3/modules/subscriptions/SubscriptionsDisabledPage';
import PricingConfigPage from '../admin-v3/modules/pricing/PricingConfigPage';
// Driver Compliance Domain — Phase 4 G5 (Plan §2.2 M-F-4 + M-F-13 + M-F-16).
import DriverDetailPage from '../admin-v3/modules/drivers/DriverDetailPage';
import CompliancePilotPage from '../admin-v3/modules/compliance/CompliancePilotPage';
import ComplianceActivityPage from '../admin-v3/modules/compliance/ComplianceActivityPage';
import { DEFAULT_LOCALE } from '../admin-v3/i18n';

const AdminV3 = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  // Auth guard mirrors AdminV2 (M-HealthCheck pattern: wait for authLoading
  // to finish before redirecting). <AdminRoute> in App.js already enforces
  // domain-level access; this is defence-in-depth on the user object.
  useEffect(() => {
    if (!authLoading && !user) navigate('/admin/login');
  }, [user, authLoading, navigate]);

  if (authLoading) return null;
  if (!user) return null;

  return (
    <Routes>
      <Route element={<AdminV3Shell />}>
        {/* Index redirect */}
        <Route index element={<Navigate to="overview" replace />} />

        {/* OPERATIONS group */}
        <Route path="overview"                       element={<OverviewPage />} />
        <Route path="bookings"                       element={<BookingsListPage />} />
        {/* P1-B3-2: unassigned dispatch queue (auto-assign + manual assign), migrated from legacy V1. */}
        <Route path="bookings/unassigned"            element={<UnassignedQueuePage />} />
        {/* AV3-M1 — V3-native booking detail (replaces the Phase-1 placeholder).
            Existing V3 links to /admin/bookings/:id (legacy V2) are intentionally
            NOT repointed in this phase; this route is reachable directly. */}
        <Route path="bookings/:bookingId"            element={<BookingDetailPage />} />
        <Route path="drivers"                        element={<DriversListPage />} />
        <Route path="drivers/:driverId"              element={<DriverDetailPage />} />
        {/* Phase 4 G5 — Compliance hub (Plan §2.2 M-F-13/14/15/16) */}
        <Route path="compliance"                     element={<CompliancePilotPage />} />
        <Route path="compliance/activity"            element={<ComplianceActivityPage />} />
        <Route path="onboarding"                     element={<OnboardingListPage />} />
        {/* AV3 A-13: onboarding/:id placeholder removed — onboarding rows open the canonical driver detail (drivers/:id). */}
        <Route path="operations"                     element={<OperationsLivePage />} />
        <Route path="operations/audit"               element={<OperationsAuditPage />} />
        <Route path="operations/notifications"       element={<OperationsNotificationsPage />} />
        <Route path="operations/health"              element={<OperationsHealthPage />} />
        <Route path="incidents"                      element={<IncidentsListPage />} />
        {/* AV3 A-13: incidents/:id placeholder removed — incident data is shown on the related booking detail. */}
        {/* P1-B3-1: contacts/messages migrated from legacy V1 (read + status triage). */}
        <Route path="contacts"                       element={<ContactsListPage />} />

        {/* FINANCIAL group */}
        {/* AV3 A-13: billing hub redirects to the primary billing surface (no placeholder). */}
        <Route path="billing"                        element={<Navigate to="/admin/billing/invoices" replace />} />
        <Route path="billing/invoices"               element={<BillingInvoicesPage />} />
        <Route path="billing/invoices/:invoiceId"    element={<BillingInvoiceDetailPage />} />
        <Route path="billing/avoirs"                 element={<AvoirListPage />} />
        <Route path="billing/avoirs/:avoirId"        element={<AvoirDetailPage />} />
        {/* AV3 A-13: billing/cycle + billing/vat removed — Category B (Billing/VAT, Financial Core). */}
        {/* Commercial pipeline nested under Facturation (CW-1 Devis / CW-2 Bon de commande) */}
        <Route path="billing/devis"                  element={<DevisListPage />} />
        <Route path="billing/devis/:devisId"         element={<DevisDetailPage />} />
        <Route path="billing/commande"               element={<BdcListPage />} />
        <Route path="billing/commande/:bdcId"        element={<BdcDetailPage />} />
        <Route path="partners"                       element={<PartnersListPage />} />
        {/* AV3 A-13: partners/pricing-profiles → Category B (Pricing); partners/:id detail removed (create/edit → Category B). */}
        <Route path="corporate"                      element={<CorporateListPage />} />
        {/* AV3 A-13: corporate/:id detail removed — A-6 corporate management is list-centric (modals). */}
        <Route path="financial"                      element={<FinancialHubPage />} />
        <Route path="financial/payouts"              element={<FinancialPayoutsPage />} />
        <Route path="financial/withdrawals"          element={<FinancialWithdrawalsPage />} />
        <Route path="financial/ledger"               element={<FinancialLedgerPage />} />
        <Route path="subscriptions"                  element={<SubscriptionsDisabledPage />} />

        {/* CONFIGURATION group */}
        <Route path="pricing"                        element={<PricingConfigPage />} />
        {/* AV3 A-13: messages (contact viewer) hidden — deprioritized, non-daily-ops; */}
        {/* settings removed — platform-config → Category B (Commissions HOLD). */}

        {/* Catch-all (proper 404, not a stub) */}
        <Route path="*"                              element={<ModulePlaceholder moduleKey="notFound"               locale={DEFAULT_LOCALE} />} />
      </Route>
    </Routes>
  );
};

export default AdminV3;
